import React, { useEffect, useRef, useState } from 'react';

// Dependency-free count-up that animates the leading number of a value string
// while preserving any prefix/suffix (e.g. "$48K", "+36%", "4.2×", "12,400").
const CountUp = ({ value, duration = 1600, className }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const raw = String(value);
    const match = raw.match(/([\d,]*\.?\d+)/);
    if (!match) {
      setDisplay(raw);
      return;
    }

    const numStr = match[1];
    const prefix = raw.slice(0, match.index);
    const suffix = raw.slice(match.index + numStr.length);
    const decimals = numStr.includes('.') ? numStr.split('.')[1].length : 0;
    const hasComma = numStr.includes(',');
    const target = parseFloat(numStr.replace(/,/g, ''));

    const format = (n) => {
      let s = n.toFixed(decimals);
      if (hasComma) {
        s = Number(s).toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
      }
      return `${prefix}${s}${suffix}`;
    };

    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setDisplay(format(target));
      return;
    }

    setDisplay(format(0));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplay(format(target * eased));
              if (p < 1) {
                requestAnimationFrame(tick);
              } else {
                setDisplay(format(target));
              }
            };
            requestAnimationFrame(tick);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
